import React, { useState, useEffect } from 'react';

import { ModelOutputStatus } from 'types';

interface ModelOutputStatusBadgeProps {
  status: ModelOutputStatus;
  attemptCount?: number;
  failureReason?: string;
  claimedAt?: Date;
}

const statusLabel: Record<ModelOutputStatus, string> = {
  pending: 'Pending',
  inProgress: 'In Progress',
  completed: '',
  failed: 'Extraction failed',
  unretryable: 'Cannot process document',
};

const statusClass: Record<ModelOutputStatus, string> = {
  pending: 'text-text-tertiary',
  inProgress: 'text-text-tertiary',
  completed: '',
  failed: 'text-feedback-error',
  unretryable: 'text-feedback-error',
};

export const formatElapsed = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;
    return `${minutes}m ${remaining}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export const ModelOutputStatusBadge = ({
  status,
  attemptCount,
  failureReason,
  claimedAt,
}: ModelOutputStatusBadgeProps) => {
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(() => {
    if (status === 'inProgress' && claimedAt) {
      return Math.floor((Date.now() - claimedAt.getTime()) / 1000);
    }
    return 0;
  });

  useEffect(() => {
    if (status !== 'inProgress' || !claimedAt) return;

    setElapsedSeconds(Math.floor((Date.now() - claimedAt.getTime()) / 1000));

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [status, claimedAt]);

  if (status === 'completed') return null;

  return (
    <div className={`text-xs ${statusClass[status]}`}>
      <span>{statusLabel[status]}</span>
      {status === 'inProgress' && claimedAt && (
        <span className="ml-1 text-text-tertiary">{`\u2022 ${formatElapsed(elapsedSeconds)}`}</span>
      )}
      {status === 'failed' && attemptCount !== undefined && (
        <span className="ml-1 text-text-tertiary">
          ({attemptCount} attempt{attemptCount !== 1 ? 's' : ''})
        </span>
      )}
      {status === 'unretryable' && failureReason && (
        <span className="ml-1">
          {failureReason.includes('mimeType') ? '— Unsupported file format' : '— Document cannot be processed'}
        </span>
      )}
    </div>
  );
};
